/**
 * Utility functions for FlowReportModal and FlowReport components
 * 
 * This file contains helper functions for:
 * - Generating stable row IDs for AG Grid
 * - Converting cell values to strings
 * - Formatting titles and labels
 */

import { FlowReportGridRow } from "./FlowReport";

/**
 * DetailModal Types
 * 
 * Shared type definitions for the FlowReport DetailModal components.
 * These types support both single-flow and multi-flow report modes.
 * 
 * @fileoverview
 * - FlowConfig: Configuration for a single flow from tblFirmReferenceData
 * - FlowUserAction: User action data for a specific flow record
 * - FlowActionLog: Parsed action log for a specific flow
 * - ActionLogEntry: Individual action entry from ReportLog JSON
 * - UserActionStatusOption: Dropdown option from configurable statuses
 */

/**
 * Flow configuration from tblFirmReferenceData JSON
 * 
 * This comes from the `flows` array in FlowReportConfig_{reportType}
 * 
 * @example
 * {
 *   flowId: 89,
 *   alias: "eFileRelease",
 *   displayName: "eFile Release",
 *   fields: [...],
 *   includeActions: true
 * }
 */
export interface FlowConfig {
  flowId: number;
  alias: string;
  displayName: string;
  fields?: FlowFieldConfig[];
  includeActions?: boolean;
  actionOrder?: string[];
  excludeActions?: string[];
}

/**
 * Field configuration within a flow
 */
export interface FlowFieldConfig {
  source: string;
  header: string;
  type?: string;
  visible?: boolean;
  sortable?: boolean;
  filterable?: boolean;
  filterValues?: string[];
  width?: number;
  flex?: number;
}

/**
 * User action data for a specific flow record
 * 
 * Used by UserActionSection to display and update user-defined
 * status and comments per FlowOrchestratorReportId
 * 
 * @property flowAlias - Unique identifier for the flow (e.g., "eFileRelease")
 * @property flowDisplayName - Human-readable name (e.g., "eFile Release")
 * @property reportId - FlowOrchestratorReportId from tblFlowOrchestratorReport
 * @property status - Current UserActionStatus value
 * @property comment - Current UserComment value
 */
export interface FlowUserAction {
  flowAlias: string;
  flowDisplayName: string;
  reportId: number | null;
  status: string;
  comment: string;
}


export interface ActionLogEntry {
  flowAlias: string;
  flowDisplayName: string;
  action: string;
  status: string;
  message?: string;
  timestamp?: string | number;
}

export interface FlowUserAction {
  flowAlias: string;
  flowDisplayName: string;
  reportId: number | null;
  status: string;
  comment: string;
}

export interface DetailModalBodyProps {
  row: Record<string, any>;
  flowsConfig: FlowConfig[];
  reportMode: "single" | "multi";
  beautifiedNames?: Record<string, string>;
  onUserActionUpdate?: (
    reportId: number,
    status: string,
    comment: string
  ) => void;
  onRefresh?: () => void;
}

export interface ClientDetailsSectionProps {
  row: Record<string, any>;
  flowsConfig: FlowConfig[];
  reportMode: "single" | "multi";
}

/**
 * Parsed action log for a specific flow
 * 
 * Used by ActionLogSection to display workflow actions
 */
export interface FlowActionLog {
  flowAlias: string;
  flowDisplayName: string;
  actions: ActionLogEntry[];
}

/**
 * Individual action entry from ReportLog JSON
 * 
 * ReportLog structure in tblFlowOrchestratorReport:
 * [
 *   { "Action": "ELFReleaseReturn", "Status": "Success", "Timestamp": 1771617793 },
 *   { "Action": "Task Moved to Status", "Status": "Success", "Message": "..." }
 * ]
 */
export interface ActionLogEntry {
  action: string;
  status: string;
  message?: string;
  timestamp?: string | number;
}

/**
 * User action status dropdown option
 * 
 * Loaded from tblFirmReferenceData with ReferenceDataCode = 'FlowReportUserActionStatuses'
 * 
 * @example
 * { "label": "Resolved", "value": "Resolved" }
 */
export interface UserActionStatusOption {
  label: string;
  value: string;
}

/**
 * Props for DetailModalBody component
 */
export interface DetailModalBodyProps {
  /** Row data from the grid */
  row: Record<string, any>;
  /** Flow configurations (empty array for single-flow) */
  flowsConfig: FlowConfig[];
  /** Report mode */
  reportMode: "single" | "multi";
  /** Beautified action names mapping */
  beautifiedNames?: Record<string, string>;
  /** Callback when user action is updated */
  onUserActionUpdate?: (reportId: number, status: string, comment: string) => void;
  /** Callback to refresh grid data */
  onRefresh?: () => void;
}

/**
 * Props for UserActionSection component
 */
export interface UserActionSectionProps {
  /** Array of flow user action data */
  flows: FlowUserAction[];
  /** Callback when a flow's status/comment is updated */
  onUpdate?: (flowAlias: string, reportId: number, status: string, comment: string) => void;
}

/**
 * Props for ActionLogSection component
 */
export interface ActionLogSectionProps {
  /** Array of flow action logs */
  flowActionLogs: FlowActionLog[];
  /** Report mode (affects display) */
  reportMode: "single" | "multi";
  /** Beautified action names mapping */
  beautifiedNames?: Record<string, string>;
}

/**
 * Props for ClientDetailsSection component
 */
export interface ClientDetailsSectionProps {
  /** Row data containing client info */
  row: Record<string, any>;
  /** Flow configurations for multi-flow field display */
  flowsConfig: FlowConfig[];
  /** Report mode */
  reportMode: "single" | "multi";
}

/**
 * DetailModal Constants
 * 
 * Shared constants for the FlowReport DetailModal components.
 * 
 * @fileoverview
 * - STATUS_COLORS: Color mapping for UserActionStatus badge display
 * - ACTION_STATUS_ICONS: Icons for action log status display
 */

/**
 * Color mapping for UserActionStatus values
 * 
 * These colors are used in:
 * - UserActionSection dropdown border
 * - Grid cell renderer badges
 * - Status indicators throughout the UI
 * 
 * Colors follow the application's design system:
 * - Green (#10B981): Positive/completed states
 * - Blue (#3B82F6): Informational/resolved states
 * - Yellow (#F59E0B): Warning/pending states
 * - Red (#EF4444): Error/escalated states
 * - Gray (#6B7280): Neutral/skipped states
 * - Purple (#8B5CF6): Hold/paused states
 */
export const STATUS_COLORS: Record<string, string> = {
  "Pending Review": "#F59E0B",
  "Skipped": "#6B7280",
  "Processed": "#10B981",
  "Resolved": "#3B82F6",
  "Escalated": "#EF4444",
  "On Hold": "#8B5CF6",
};

/**
 * Default color when status is not found in STATUS_COLORS
 */
export const DEFAULT_STATUS_COLOR = "#6B7280";

/**
 * Get color for a status value
 * 
 * @param status - The UserActionStatus value
 * @returns Hex color code
 */
export const getStatusColor = (status: string): string => {
  return STATUS_COLORS[status] || DEFAULT_STATUS_COLOR;
};

/**
 * Action status configuration for ActionLogSection
 * 
 * Maps action status values to display properties
 */
export const ACTION_STATUS_CONFIG: Record<string, { icon: string; className: string }> = {
  success: { icon: "✓", className: "successIcon" },
  fail: { icon: "✗", className: "failIcon" },
  failure: { icon: "✗", className: "failIcon" },
  error: { icon: "✗", className: "failIcon" },
  pending: { icon: "○", className: "pendingIcon" },
};

/**
 * Default action status config for unknown statuses
 */
export const DEFAULT_ACTION_STATUS_CONFIG = { icon: "○", className: "pendingIcon" };

/**
 * Get action status display config
 * 
 * @param status - The action status from ReportLog
 * @returns Icon and className for display
 */
export const getActionStatusConfig = (status: string): { icon: string; className: string } => {
  return ACTION_STATUS_CONFIG[status?.toLowerCase()] || DEFAULT_ACTION_STATUS_CONFIG;
};

/**
 * API endpoints used by DetailModal components
 */
export const DETAIL_MODAL_ENDPOINTS = {
  UPDATE_RECORD_STATUS: "/workflow/flow/record/update-status/",
  GET_USER_ACTION_STATUSES: "/workflow/flow/user-action-statuses/",
};

/**
 * Generates a stable row ID for AG Grid from row data
 * 
 * AG Grid requires a unique, stable ID for each row to properly handle
 * updates, sorting, and filtering. This function creates an ID by combining
 * multiple fields to ensure uniqueness.
 * 
 * @param data - The row data object
 * @returns A stable string ID for the row
 * 
 * @example
 * ```ts
 * const rowId = getStableRowId({ 
 *   "Client Id": "12345", 
 *   "Client Name": "John Doe" 
 * });
 * // Returns: "12345-john-doe"
 * ```
 */
export const getStableRowId = (data: FlowReportGridRow): string => {
  // Try to use Client Id as primary identifier
  const clientId = data["Client Id"];
  const clientName = data["Client Name"];
  const dateSigned = data["Date 8879 Signed"];

  // Build a composite key from available fields
  const parts: string[] = [];

  if (clientId !== null && clientId !== undefined) {
    parts.push(String(clientId).trim());
  }

  if (clientName !== null && clientName !== undefined) {
    parts.push(
      String(clientName)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "-")
        .replace(/-+/g, "-")
        .trim()
    );
  }

  if (dateSigned !== null && dateSigned !== undefined) {
    parts.push(
      String(dateSigned)
        .replace(/[^a-z0-9]/gi, "-")
        .replace(/-+/g, "-")
        .trim()
    );
  }

  // If we have parts, join them; otherwise use a fallback
  if (parts.length > 0) {
    return parts.join("-");
  }

  // Fallback: use a hash of the entire row data
  return `row-${hashCode(JSON.stringify(data))}`;
};

/**
 * Converts any cell value to a string representation
 * 
 * Handles various data types and ensures consistent string output
 * for display in tables and grids.
 * 
 * @param value - The cell value to stringify
 * @returns A string representation of the value
 * 
 * @example
 * ```ts
 * stringifyCell(null) // ""
 * stringifyCell(123) // "123"
 * stringifyCell(true) // "Yes"
 * stringifyCell(false) // "No"
 * stringifyCell({ name: "John" }) // '{"name":"John"}'
 * ```
 */
export const stringifyCell = (value: any): string => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return "";
  }

  // Handle booleans
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  // Handle numbers
  if (typeof value === "number") {
    return String(value);
  }

  // Handle strings
  if (typeof value === "string") {
    return value;
  }

  // Handle dates
  if (value instanceof Date) {
    return value.toISOString();
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.map(stringifyCell).join(", ");
  }

  // Handle objects (convert to JSON)
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }

  // Default fallback
  return String(value);
};

/**
 * Converts a string to title case
 * 
 * Transforms a string into title case format where the first letter
 * of each word is capitalized.
 * 
 * @param str - The string to convert
 * @returns The title-cased string
 * 
 * @example
 * ```ts
 * toTitle("hello world") // "Hello World"
 * toTitle("HELLO WORLD") // "Hello World"
 * toTitle("hello-world") // "Hello-World"
 * ```
 */
export const toTitle = (str: string): string => {
  if (!str) return "";

  return str
    .split(" ")
    .map((word) => {
      if (word.length === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};

/**
 * Formats a field name to a display label
 * 
 * Converts database field names (snake_case, camelCase) to readable labels.
 * 
 * @param fieldName - The field name to format
 * @returns A formatted display label
 * 
 * @example
 * ```ts
 * formatFieldName("client_name") // "Client Name"
 * formatFieldName("clientName") // "Client Name"
 * formatFieldName("DMS_UPLOAD_STATUS") // "DMS Upload Status"
 * ```
 */
export const formatFieldName = (fieldName: string): string => {
  if (!fieldName) return "";

  // Handle snake_case
  let formatted = fieldName.replace(/_/g, " ");

  // Handle camelCase
  formatted = formatted.replace(/([a-z])([A-Z])/g, "$1 $2");

  // Handle all caps acronyms (like DMS, API)
  formatted = formatted.replace(/([A-Z])([A-Z][a-z])/g, "$1 $2");

  // Convert to title case
  return toTitle(formatted);
};

/**
 * Generates a simple hash code from a string
 * 
 * Used internally for creating fallback IDs when no primary key is available.
 * Not cryptographically secure - only for generating unique-ish IDs.
 * 
 * @param str - The string to hash
 * @returns A numeric hash code
 * 
 * @internal
 */
const hashCode = (str: string): number => {
  let hash = 0;
  if (str.length === 0) return hash;

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return Math.abs(hash);
};

/**
 * Formats a date string to a locale-specific format
 * 
 * @param dateString - ISO date string or Date object
 * @param locale - The locale to use (default: 'en-US')
 * @returns Formatted date string
 * 
 * @example
 * ```ts
 * formatDate("2024-01-15T10:30:00Z") // "1/15/2024, 10:30:00 AM"
 * formatDate("2024-01-15") // "1/15/2024"
 * ```
 */
export const formatDate = (
  dateString: string | Date,
  locale: string = "en-US"
): string => {
  if (!dateString) return "";

  try {
    const date = typeof dateString === "string" ? new Date(dateString) : dateString;
    
    if (isNaN(date.getTime())) {
      return String(dateString);
    }

    // If time is midnight, show only date
    if (date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0) {
      return date.toLocaleDateString(locale);
    }

    // Otherwise show both date and time
    return date.toLocaleString(locale);
  } catch (error) {
    return String(dateString);
  }
};

/**
 * Formats a number with thousand separators
 * 
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted number string
 * 
 * @example
 * ```ts
 * formatNumber(1234567) // "1,234,567"
 * formatNumber(1234.5678, 2) // "1,234.57"
 * ```
 */
export const formatNumber = (value: number, decimals: number = 0): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "";
  }

  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Formats a percentage value
 * 
 * @param value - The percentage value (e.g., 25 for 25%)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 * 
 * @example
 * ```ts
 * formatPercent(25) // "25.0%"
 * formatPercent(33.333, 2) // "33.33%"
 * ```
 */
export const formatPercent = (value: number, decimals: number = 1): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "";
  }

  return `${value.toFixed(decimals)}%`;
};

/**
 * Truncates a string to a maximum length with ellipsis
 * 
 * @param str - The string to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated string with ellipsis if needed
 * 
 * @example
 * ```ts
 * truncate("This is a long string", 10) // "This is a..."
 * truncate("Short", 10) // "Short"
 * ```
 */
export const truncate = (str: string, maxLength: number): string => {
  if (!str) return "";
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength - 3) + "...";
};

/**
 * Checks if a value is empty (null, undefined, empty string, or empty array)
 * 
 * @param value - The value to check
 * @returns True if the value is empty
 * 
 * @example
 * ```ts
 * isEmpty(null) // true
 * isEmpty("") // true
 * isEmpty([]) // true
 * isEmpty("hello") // false
 * isEmpty([1, 2, 3]) // false
 * ```
 */
export const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

/**
 * Sanitizes a string for use in HTML
 * 
 * Escapes HTML special characters to prevent XSS attacks
 * 
 * @param str - The string to sanitize
 * @returns Sanitized string safe for HTML
 * 
 * @example
 * ```ts
 * sanitizeHtml("<script>alert('xss')</script>") 
 * // "&lt;script&gt;alert('xss')&lt;/script&gt;"
 * ```
 */
export const sanitizeHtml = (str: string): string => {
  if (!str) return "";

  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#x27;",
    "/": "&#x2F;",
  };

  return str.replace(/[&<>"'/]/g, (char) => map[char]);
};

/**
 * Unified date formatting function for the entire FlowReport module
 * Handles: ISO strings, Unix timestamps (seconds), Date objects
 * Converts UTC to browser's local timezone with AM/PM format
 * 
 * @param value - ISO date string, Unix timestamp (seconds), or Date object
 * @param includeSeconds - Whether to include seconds (default: true)
 * @returns Formatted date string (MM/DD/YYYY h:mm:ss AM/PM) or empty string
 */
export const formatDateLocal = (value: any, includeSeconds: boolean = true): string => {
  if (!value) return "";

  try {
    let date: Date;

    if (typeof value === "number") {
      // Unix timestamp - check if seconds or milliseconds
      // Timestamps after year 2001 in ms are > 10 digits
      date = value > 9999999999 
        ? new Date(value)        // Already milliseconds
        : new Date(value * 1000); // Convert seconds to ms
    } else if (typeof value === "string") {
      date = new Date(value);
    } else if (value instanceof Date) {
      date = value;
    } else {
      return "";
    }

    if (isNaN(date.getTime())) {
      return "";
    }

    // Format in browser's local timezone with AM/PM
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    let hours = date.getHours();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12

    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    if (includeSeconds) {
      return `${month}/${day}/${year} ${hours}:${minutes}:${seconds} ${ampm}`;
    }
    return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
  } catch {
    return "";
  }
};

/**
 * Short date only (no time)
 */
export const formatDateOnlyLocal = (value: any): string => {
  if (!value) return "";
  try {
    let date: Date;

    if (typeof value === "number") {
      date = value > 9999999999 
        ? new Date(value) 
        : new Date(value * 1000);
    } else if (typeof value === "string") {
      date = new Date(value);
    } else if (value instanceof Date) {
      date = value;
    } else {
      return "";
    }

    if (isNaN(date.getTime())) return "";
    
    return date.toLocaleDateString(undefined, {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  } catch {
    return "";
  }
};
/**
 * Format a UTC date string to local timezone with seconds
 */
export const formatDateLocalFull = (value: any): string => {
  if (!value) return "";

  try {
    let date: Date;

    if (typeof value === "number") {
      date = new Date(value * 1000);
    } else if (typeof value === "string") {
      date = new Date(value);
    } else if (value instanceof Date) {
      date = value;
    } else {
      return "";
    }

    if (isNaN(date.getTime())) {
      return "";
    }

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds} ${ampm}`;
  } catch {
    return "";
  }
};

/**
 * Format date for display with timezone indicator
 */
export const formatDateWithTimezone = (value: any): string => {
  if (!value) return "";

  try {
    let date: Date;

    if (typeof value === "number") {
      date = new Date(value * 1000);
    } else if (typeof value === "string") {
      date = new Date(value);
    } else if (value instanceof Date) {
      date = value;
    } else {
      return "";
    }

    if (isNaN(date.getTime())) {
      return "";
    }

    // Get timezone abbreviation
    const tzAbbr = date
      .toLocaleTimeString("en-US", { timeZoneName: "short" })
      .split(" ")
      .pop();

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    
    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds} ${ampm} ${tzAbbr}`;
  } catch {
    return "";
  }
};

export const getStartOfYear = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), 0, 1);
};

export const getToday = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

export const toDateOnlyIso = (d: Date | null): string => {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Get the user's local timezone abbreviation (e.g., "IST", "EST", "CDT", "GMT")
 * Falls back to offset format if abbreviation not available
 */
export const getTimezoneDisplay = (): string => {
  try {
    const now = new Date();
    
    // Get offset in minutes and convert to hours/minutes
    const offsetMinutes = now.getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetMins = Math.abs(offsetMinutes) % 60;
    const sign = offsetMinutes <= 0 ? "+" : "-";
    
    // Format offset string
    const offsetStr = offsetMins > 0 
      ? `UTC${sign}${offsetHours}:${String(offsetMins).padStart(2, "0")}`
      : `UTC${sign}${offsetHours}`;

    // Map IANA timezone names to abbreviations
    const timezoneAbbreviations: Record<string, string> = {
      "Asia/Kolkata": "IST",
      "Asia/Calcutta": "IST",
      "America/New_York": offsetMinutes === 240 ? "EDT" : "EST",
      "America/Chicago": offsetMinutes === 300 ? "CDT" : "CST",
      "America/Denver": offsetMinutes === 360 ? "MDT" : "MST",
      "America/Los_Angeles": offsetMinutes === 420 ? "PDT" : "PST",
      "Europe/London": offsetMinutes === 0 ? "GMT" : "BST",
      "Europe/Paris": offsetMinutes === -60 ? "CET" : "CEST",
      "Asia/Tokyo": "JST",
      "Asia/Shanghai": "CST",
      "Asia/Dubai": "GST",
      "Asia/Singapore": "SGT",
      "Australia/Sydney": offsetMinutes === -600 ? "AEST" : "AEDT",
    };

    const ianaTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const abbreviation = timezoneAbbreviations[ianaTimezone];

    if (abbreviation) {
      return `${abbreviation} (${offsetStr})`;
    }

    // Fallback: just show offset
    return offsetStr;
  } catch {
    return "local time";
  }
};